import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { TicketCheck, Plus, Clock, X, Send } from 'lucide-react';
import { PremiumPageHeader } from '@repo/ui';

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  'in-progress': 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  resolved: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  closed: 'bg-muted text-muted-foreground',
};

const TicketsPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');

  const { data: tickets, isLoading } = useQuery({
    queryKey: ['student-tickets'],
    queryFn: () => api.get('/student/tickets').then((res) => res.data?.data),
  });

  const mutation = useMutation({
    mutationFn: (data: { subject: string; description: string }) =>
      api.post('/student/tickets', data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['student-tickets'] });
      closeModal();
    },
  });

  const closeModal = () => {
    setIsModalOpen(false);
    setSubject('');
    setDescription('');
    mutation.reset();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) return;
    mutation.mutate({ subject: subject.trim(), description: description.trim() });
  };

  return (
    <div className="space-y-6">
      <PremiumPageHeader
        title={t('student.tickets.title')}
        subtitle={t('student.tickets.subtitle', {
          defaultValue:
            'Need help? Create a support ticket and our team will get back to you shortly.',
        })}
        icon={TicketCheck}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      >
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary text-primary-foreground text-sm font-black hover:bg-primary/90 transition-all shadow-xl shadow-primary/20 active:scale-95"
        >
          <Plus className="h-4 w-4" />
          {t('student.tickets.create')}
        </button>
      </PremiumPageHeader>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : !tickets?.length ? (
        <div className="text-center py-16 text-muted-foreground">{t('student.tickets.empty')}</div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket: any) => (
            <div
              key={ticket._id}
              className="rounded-xl border border-border bg-card p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{ticket.subject}</h3>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {ticket.description}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${STATUS_COLORS[ticket.status] || 'bg-muted text-muted-foreground'}`}
                >
                  {ticket.status}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {new Date(ticket.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Ticket Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />

          {/* Modal */}
          <div className="relative w-full max-w-lg mx-4 rounded-2xl border border-border bg-card shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-0">
              <h2 className="text-xl font-bold">{t('student.tickets.createTitle')}</h2>
              <button
                onClick={closeModal}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="ticket-subject">
                  {t('student.tickets.subject')}
                </label>
                <input
                  id="ticket-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('student.tickets.subjectPlaceholder') as string}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow"
                  maxLength={200}
                  autoFocus
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="ticket-description">
                  {t('student.tickets.description')}
                </label>
                <textarea
                  id="ticket-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('student.tickets.descPlaceholder') as string}
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-sm text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow"
                  maxLength={2000}
                  required
                />
                <p className="text-xs text-muted-foreground text-end">
                  {description.length} / 2000
                </p>
              </div>

              {mutation.isError && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                  {t('student.tickets.error')}
                </div>
              )}

              <div className="flex items-center gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={mutation.isPending || !subject.trim() || !description.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="h-4 w-4" />
                  {mutation.isPending ? t('common.loading') : t('student.tickets.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
